// Libs
import React from 'react';
import { connect } from 'react-redux';
// Utils
import CoreUtils from '../utils';
import { debounce } from 'utils/libs';
// Actions
import {
	cancelLatestDatasourceRequest,
	mutate1Object as mutate1ObjectInCore,
	getLatestDataFromProcessedSQL,
	setDatasource,
	resetDatasource,
} from '../actions';
// Selectors
import {
	selectSelectedProjectId,
	selectSelectedOrderTargetFilterId,
	selectSelectedServiceIds,
	selectSelectedDataFilterId,
	selectDataFilters,
	selectDataFilterUserParams,
	selectSelectedDataFilterLastRowId,
	selectReloadDatasource,
	selectSearchDatasource,
} from 'core/selectors';
import { selectSelectedSubModule } from 'components/Modules/selectors';
// Filter pickers
import {
	ProjectPicker,
	OrderTargetFilterPicker,
	ServicePicker,
	DatasourceAccessPicker,
	ParamFilterPicker,
	ModulePicker,
} from './filterPickers';

export const _DatasourceBuilder = ({
	// State
	selectedProjectId,
	selectedOrderTargetFilterId,
	selectedServiceIds,
	selectedSubModule,
	searchDatasource,
	dataFilters,
	selectedDataFilterId,
	isReloadDatasource,
	dataFilterLastRowId,
	dataFilterUserParams,
	// Actions
	cancelLatestDatasourceRequest,
	mutate1ObjectInCore,
	getLatestDataFromProcessedSQL,
	setDatasource,
	resetDatasource,
}) => {
	const datasourceScopeId = selectedSubModule?.id;

	React.useEffect(() => {
		resetDatasource();
		return () => cancelLatestDatasourceRequest();
	}, [cancelLatestDatasourceRequest, datasourceScopeId, resetDatasource]);

	// Get data filter config
	const dataFilterItem = React.useMemo(
		() => dataFilters.find(df => df.id === selectedDataFilterId),
		[selectedDataFilterId, dataFilters],
	);
	const dataFilterItemSignature = JSON.stringify({
		id: dataFilterItem?.id,
		limit: dataFilterItem?.config?.limit,
		params: dataFilterItem?.params,
		filters: dataFilterItem?.filters?.map(({ param, required, state }) => ({
			param,
			required,
			state,
		})),
	});
	const selectedServiceIdsSignature = JSON.stringify(selectedServiceIds);
	const effectiveUserParams = React.useMemo(
		() => ({
			...dataFilterUserParams,
			selectedProjectId,
			selectedOrderTargetFilterId,
			selectedServiceIds,
		}),
		[
			dataFilterUserParams,
			selectedProjectId,
			selectedOrderTargetFilterId,
			selectedServiceIdsSignature,
		],
	);

	// Get onEventActions
	const onEventActions = React.useMemo(
		() =>
			CoreUtils.onGettedDataEvents({
				dataFilterLastRowId,
				mutate1ObjectInCore,
				setDatasource,
				resetDatasource,
			}),
		[dataFilterLastRowId, mutate1ObjectInCore, resetDatasource, setDatasource],
	);

	// Must fetch data
	const fetchDebouncedData = React.useCallback(
		debounce(callback => callback(), 200),
		[],
	);

	React.useEffect(() => {
		fetchDebouncedData(() => {
			if (
				CoreUtils.checkMustFetchData({
					isReloadDatasource,
					// User params
					dataFilterUserParams: effectiveUserParams,
					subDataFilters: dataFilterItem?.filters,
					datasourceParams: dataFilterItem?.params,
				})
			) {
				mutate1ObjectInCore('datasource', { reload: false });
				getLatestDataFromProcessedSQL({
					selectedDataFilterId,
					limit: dataFilterItem?.config?.limit,
					lastRowId: dataFilterLastRowId,
					searchValue: searchDatasource.value,
					userParams: effectiveUserParams,
					...onEventActions,
				});
			}
		});
		return () => fetchDebouncedData.cancel();
	}, [
		dataFilterItemSignature,
		dataFilterLastRowId,
		effectiveUserParams,
		fetchDebouncedData,
		getLatestDataFromProcessedSQL,
		isReloadDatasource,
		mutate1ObjectInCore,
		onEventActions,
		searchDatasource.value,
		selectedDataFilterId,
	]);

	return (
		<>
			{/* MODULES */}
			<ModulePicker />
			{selectedSubModule && (
				<>
					{/* PROJECTS */}
					<ProjectPicker moduleItemId={selectedSubModule.id} />
					{/* ORDER TARGETS */}
					<OrderTargetFilterPicker moduleItemId={selectedSubModule.id} />
					{/* SERVICES */}
					<ServicePicker />
					{/* CUSTOM DATA FILTERS */}
					<DatasourceAccessPicker moduleItemId={selectedSubModule.id} />
					{/* CUSTOM SUB DATA FILTERS */}
					<ParamFilterPicker />
				</>
			)}
		</>
	);
};

const mapStateToProps = state => ({
	selectedProjectId: selectSelectedProjectId(state),
	selectedOrderTargetFilterId: selectSelectedOrderTargetFilterId(state),
	selectedServiceIds: selectSelectedServiceIds(state),
	searchDatasource: selectSearchDatasource(state),
	dataFilters: selectDataFilters(state),
	selectedDataFilterId: selectSelectedDataFilterId(state),
	isReloadDatasource: selectReloadDatasource(state),
	dataFilterLastRowId: selectSelectedDataFilterLastRowId(state),
	dataFilterUserParams: selectDataFilterUserParams(state),
	selectedSubModule: selectSelectedSubModule(state),
});

const actions = {
	cancelLatestDatasourceRequest,
	mutate1ObjectInCore,
	getLatestDataFromProcessedSQL,
	setDatasource,
	resetDatasource,
};

export default connect(mapStateToProps, actions)(_DatasourceBuilder);
